
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Target,
  Calendar,
  Users,
  BellDot,
  Rocket,
  TagIcon,
  History,
  Bell,
  Award,
  Mail,
  ShoppingCart,
  DollarSign
} from "lucide-react";
import { Customer, getAllCustomers } from "@/lib/db/customerDb";

interface CampaignSuggestion {
  id: string;
  title: string;
  description: string;
  targetGroup: string;
  targetCount: number;
  suggestedProducts: string[];
  estimatedConversion: string;
}

const CampaignTargetingTab = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [campaignSuggestions, setCampaignSuggestions] = useState<CampaignSuggestion[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await getAllCustomers();
        setCustomers(data);
        generateCampaignSuggestions(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load customer data", error);
        toast({
          variant: "destructive",
          title: "Error loading data",
          description: "Could not load customer data for campaign targeting.",
        });
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [toast]);

  const generateCampaignSuggestions = (customerData: Customer[]) => {
    // Get inactive customers (no purchases in last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const inactiveCustomers = customerData.filter(customer => {
      const lastPurchase = new Date(customer.lastPurchaseDate);
      return lastPurchase < thirtyDaysAgo;
    });
    
    // Get high-value customers
    const highValueCustomers = customerData.filter(
      customer => customer.totalSpent > 3000 || customer.loyaltyTier === 'Gold' || customer.loyaltyTier === 'Platinum'
    );
    
    // Get frequent shoppers
    const frequentShoppers = customerData.filter(
      customer => customer.visitFrequency >= 6
    );
    
    // Generate campaign suggestions
    const suggestions: CampaignSuggestion[] = [
      {
        id: "1",
        title: "Re-engagement Campaign",
        description: "Win back inactive customers with special offers",
        targetGroup: "Inactive Customers",
        targetCount: inactiveCustomers.length,
        suggestedProducts: ["Household Essentials", "Pantry Items", "Personal Care"],
        estimatedConversion: "15-20%"
      },
      {
        id: "2",
        title: "VIP Appreciation",
        description: "Exclusive discounts for our most valuable customers",
        targetGroup: "High-Value Customers",
        targetCount: highValueCustomers.length,
        suggestedProducts: ["Premium Products", "Gift Sets", "Special Imports"],
        estimatedConversion: "30-35%"
      },
      {
        id: "3",
        title: "Frequent Shopper Rewards",
        description: "Double points promotion for loyal customers",
        targetGroup: "Frequent Shoppers",
        targetCount: frequentShoppers.length,
        suggestedProducts: ["Daily Essentials", "Fresh Produce", "Bakery Items"],
        estimatedConversion: "40-45%"
      },
      {
        id: "4",
        title: "Silver Tier Upgrade Campaign",
        description: "Help Silver members reach Gold status with bonus points",
        targetGroup: "Silver Tier Members",
        targetCount: customerData.filter(c => c.loyaltyTier === 'Silver').length,
        suggestedProducts: ["High-Margin Items", "Loyalty Program Boosters"],
        estimatedConversion: "25-30%"
      }
    ];
    
    setCampaignSuggestions(suggestions);
  };

  const handleCreateCampaign = (suggestion: CampaignSuggestion) => {
    toast({
      title: "Campaign initiated",
      description: `${suggestion.title} campaign has been set up for ${suggestion.targetCount} customers.`,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Target className="text-muted-foreground h-5 w-5" />
        <h2 className="text-xl font-semibold">Campaign Targeting</h2>
      </div>
      
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
        {/* Customer Insights */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Segments Overview</CardTitle>
            <CardDescription>Current customer distribution by key segments</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span>Total Customers</span>
              </div>
              <Badge variant="secondary">{customers.length}</Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <BellDot className="h-4 w-4 text-orange-500" />
                <span>Need Re-engagement</span>
              </div>
              <Badge variant="outline" className="text-orange-500">
                {customers.filter(c => {
                  const lastPurchase = new Date(c.lastPurchaseDate);
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  return lastPurchase < thirtyDaysAgo;
                }).length}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-purple-500" />
                <span>VIP / High Value</span>
              </div>
              <Badge variant="outline" className="text-purple-500">
                {customers.filter(c => c.totalSpent > 3000 || c.loyaltyTier === 'Gold' || c.loyaltyTier === 'Platinum').length}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-green-500" />
                <span>Frequent Shoppers</span>
              </div>
              <Badge variant="outline" className="text-green-500">
                {customers.filter(c => c.visitFrequency >= 6).length}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-center gap-2">
                <TagIcon className="h-4 w-4 text-red-500" />
                <span>Tagged for Campaigns</span>
              </div>
              <Badge variant="outline" className="text-red-500">
                {customers.filter(c => c.tags.length > 0).length}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Engagement Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Engagement Metrics</CardTitle>
            <CardDescription>Performance of recent customer engagements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-blue-500" />
                <span>SMS Open Rate</span>
              </div>
              <Badge variant="secondary">28%</Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-purple-500" />
                <span>Email Open Rate</span>
              </div>
              <Badge variant="secondary">22%</Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-green-500" />
                <span>Promotion Redemption Rate</span>
              </div>
              <Badge variant="secondary">18%</Badge>
            </div>
            
            <div className="flex justify-between items-center border-b pb-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" />
                <span>Last Campaign Response</span>
              </div>
              <Badge variant="outline">32%</Badge>
            </div>
            
            <div className="flex justify-between items-center pb-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-red-500" />
                <span>Avg. Campaign ROI</span>
              </div>
              <Badge variant="outline">250%</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Campaign Suggestions */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <Rocket className="text-muted-foreground h-5 w-5" />
          <h3 className="text-lg font-semibold">Campaign Suggestions</h3>
        </div>
        
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
          {campaignSuggestions.map((suggestion) => (
            <Card key={suggestion.id}>
              <CardHeader>
                <CardTitle>{suggestion.title}</CardTitle>
                <CardDescription>{suggestion.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Target Group:</span>
                    <span className="font-medium">{suggestion.targetGroup}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Target Count:</span>
                    <Badge>{suggestion.targetCount} customers</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Est. Conversion:</span>
                    <span className="font-medium text-green-600">{suggestion.estimatedConversion}</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground block mb-2">Suggested Products:</span>
                    <div className="flex flex-wrap gap-2">
                      {suggestion.suggestedProducts.map((product, idx) => (
                        <Badge key={idx} variant="outline">{product}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full" onClick={() => handleCreateCampaign(suggestion)}>
                  Create Campaign
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CampaignTargetingTab;
